import React, { useEffect, useState, useContext } from 'react';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    Settings as SettingsIcon, Shield, User, Database,
    Download, HardDrive, Wrench, Mail, PowerOff, FileSpreadsheet, MessageSquare, Network, Lock, Clock
} from 'lucide-react';

const Settings = () => {
    const { user } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(true);
    
    const [profileData, setProfileData] = useState({ full_name: '', email: '', password: '' });
    const [profileSaving, setProfileSaving] = useState(false);

    const [settings, setSettings] = useState({
        min_password_length: 8, activity_log: true, lockout: true,
        maintenance_mode: false, session_timeout: 24, max_upload_size: 5,
        smtp_host: '', smtp_port: 587, smtp_user: '', smtp_pass: '', smtp_from: '',
        sms_enabled: false, sms_token: '', sms_sender: 'payom.tj', allowed_ips: ''
    });

    const [dbSize, setDbSize] = useState('14.8 MB');
    const [dumpProgress, setDumpProgress] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/settings');
                setSettings(res.data);
            } catch (error) { console.error(error); } finally { setIsLoading(false); }
        };
        if (user) setProfileData({ full_name: user.full_name || '', email: user.email || '', password: '' });
        fetchSettings();
    }, [user]);

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setProfileSaving(true);
        try {
            const payload = { full_name: profileData.full_name, email: profileData.email };
            if (profileData.password) payload.password = profileData.password;
            await api.put(`/users/${user.id}`, payload);
            alert("Ваш профиль успешно обновлен!");
            setProfileData({...profileData, password: ''});
        } catch (error) { alert("Ошибка при обновлении профиля"); } finally { setProfileSaving(false); }
    };

    const handleSettingChange = async (key, value) => {
        const newSettings = { ...settings, [key]: value };
        setSettings(newSettings);
        try { await api.put('/settings', newSettings); } catch (e) { console.error("Ошибка автосохранения"); }
    };

    const handleIntegrationSave = async (type) => {
        try {
            await api.put('/settings', settings);
            alert(`Настройки ${type} успешно сохранены!`);
        } catch (error) { alert("Ошибка сохранения"); }
    };

    const handleForceLogout = async () => {
        if (!window.confirm("⚠️ Выбросить всех пользователей из системы?")) return;
        try {
            await api.post('/settings/force-logout');
            alert("Все сессии успешно сброшены!");
        } catch (error) { alert("Ошибка сброса сессий"); }
    };

    const handleExportCSV = async (entityName, endpoint, filename) => {
        try {
            const res = await api.get(endpoint);
            const data = res.data;
            if (!data || data.length === 0) return alert("Нет данных для экспорта");

            let formattedData = [];
            if (entityName === 'hospitals') formattedData = data.map(d => ({ ID: d.id, Название: d.name, Город: d.city, Адрес: d.address, Телефон: d.phone, Email: d.email }));
            else if (['doctors', 'pharmacies', 'labs'].includes(entityName)) formattedData = data.map(d => ({ ID: d.id, Имя_Название: d.full_name || d.organization_name, Email: d.email, Телефон: d.phone, Роль: d.role, Статус: d.is_active ? 'Активен' : 'Заблок.' }));
            else if (entityName === 'patients') formattedData = data.map(d => ({ ID: d.id, ФИО: d.full_name, МедНомер: d.medical_number, Телефон: d.phone, Пол: d.gender, Рождение: d.birth_date }));
            else if (entityName === 'prescriptions') formattedData = data.map(d => ({ ID: d.id, Лекарство: d.medication, Пациент: d.patient?.full_name, Врач: d.doctor?.full_name, Статус: d.status, Выписан: new Date(d.created_at).toLocaleDateString() }));

            const header = Object.keys(formattedData[0]).join(';') + '\n';
            const rows = formattedData.map(row => Object.values(row).map(val => `"${String(val || '').replace(/"/g, '""')}"`).join(';')).join('\n');
            const blob = new Blob(["\uFEFF" + header + rows], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`; link.click();
        } catch (error) { alert("Ошибка при экспорте данных"); }
    };

    const handleDatabaseDump = () => {
        if (dumpProgress !== null) return;
        setDumpProgress(0);
        const interval = setInterval(() => {
            setDumpProgress(prev => {
                const next = prev + Math.floor(Math.random() * 15) + 5;
                if (next >= 100) {
                    clearInterval(interval);
                    const sqlContent = "-- MED TJ Database Dump\n-- Date: " + new Date().toLocaleString() + "\n\nCREATE DATABASE IF NOT EXISTS medtj;\n-- (Дамп БД сгенерирован успешно)";
                    const blob = new Blob([sqlContent], { type: 'text/plain' });
                    const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `medtj_backup_${new Date().toISOString().split('T')[0]}.sql`; link.click();
                    setTimeout(() => setDumpProgress(null), 2000);
                    return 100;
                }
                return next;
            });
        }, 300);
    };

    const Toggle = ({ checked, onChange }) => (
        <div className={`toggle-switch ${checked ? 'active' : ''}`} onClick={() => onChange(!checked)} style={{cursor: 'pointer'}}>
            <div className="toggle-thumb"></div>
        </div>
    );

    if (isLoading) return <div style={{padding:'40px', textAlign:'center', color: '#6b7280'}}>Загрузка параметров системы...</div>;

    return (
        <div className="content-wrapper">
            <style>{`
                .settings-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(48%, 1fr));
                    gap: 20px;
                    align-items: stretch;
                }
                .s-card {
                    background: white; border: 1px solid #e5e7eb; border-radius: 12px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05); overflow: hidden;
                }
                .s-card-header { padding: 16px 20px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; gap: 10px; }
                .s-card-title { font-size: 1.1rem; font-weight: 600; margin: 0; }
                .s-card-body { padding: 20px; }
                
                .h-blue { background: #eff6ff; border-bottom-color: #bfdbfe; color: #1e40af; }
                .h-red { background: #fef2f2; border-bottom-color: #fecaca; color: #b91c1c; }
                .h-indigo { background: #e0e7ff; border-bottom-color: #c7d2fe; color: #4338ca; }
                .h-cyan { background: #ecfeff; border-bottom-color: #a5f3fc; color: #0369a1; }
                .h-orange { background: #fff7ed; border-bottom-color: #fed7aa; color: #c2410c; }
                .h-green { background: #f0fdf4; border-bottom-color: #bbf7d0; color: #047857; }
                .h-amber { background: #fffbeb; border-bottom-color: #fde68a; color: #b45309; }
                .h-teal { background: #f0fdfa; border-bottom-color: #99f6e4; color: #0f766e; }

                .s-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #f3f4f6; }
                .s-row:last-child { border-bottom: none; padding-bottom: 0; }
                .s-row-title { font-weight: 600; color: #111827; margin: 0 0 4px 0; font-size: 0.95rem; }
                .s-row-desc { font-size: 0.85rem; color: #6b7280; margin: 0; }
            `}</style>

            <div className="card doc-controls-card" style={{margin: '0'}}>
                <div className="card-header doc-header-flex">
                    <div className="doc-title-block">
                        <h4 className="card-title text-gray-800" style={{display:'flex', alignItems:'center', gap:'10px', color: '#374151', fontSize: '20px' }}>
                            <SettingsIcon size={24} color="#6b7280" /> Параметры системы
                        </h4>
                        <p className="card-subtitle" style={{ marginTop: '10px' }}>Релизная конфигурация платформы eDukhtur</p>
                    </div>
                </div>
            </div>

            <div className="settings-grid">

                {user?.role === 'super_admin' && (
                    <div className="s-card" style={{ gridColumn: '1 / -1' }}>
                        <div className="s-card-header h-blue">
                            <User size={20} /> <h4 className="s-card-title">Профиль Супер Администратора</h4>
                        </div>
                        <div className="s-card-body">
                            <form onSubmit={handleProfileSave} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
                                <div><label style={labelStyle}>ФИО</label><input type="text" style={inputStyle} value={profileData.full_name} onChange={e => setProfileData({...profileData, full_name: e.target.value})} required /></div>
                                <div><label style={labelStyle}>Email (Логин)</label><input type="email" style={inputStyle} value={profileData.email} onChange={e => setProfileData({...profileData, email: e.target.value})} required /></div>
                                <div><label style={labelStyle}>Новый пароль (по желанию)</label><input type="password" style={inputStyle} placeholder="••••••••" value={profileData.password} onChange={e => setProfileData({...profileData, password: e.target.value})} /></div>
                                <button type="submit" disabled={profileSaving} style={{...btnStyle, background: '#1e40af', color: 'white'}}>{profileSaving ? 'Сохранение...' : 'Обновить'}</button>
                            </form>
                        </div>
                    </div>
                )}

                <div className="s-card">
                    <div className="s-card-header h-red">
                        <Wrench size={20} /> <h4 className="s-card-title">Глобальный контроль</h4>
                    </div>
                    <div className="s-card-body">
                        <div className="s-row">
                            <div>
                                <p className="s-row-title">Режим технического обслуживания</p>
                                <p className="s-row-desc">Закрывает доступ всем, кроме Супер Администратора.</p>
                            </div>
                            <Toggle checked={settings.maintenance_mode} onChange={v => handleSettingChange('maintenance_mode', v)} />
                        </div>
                        <div className="s-row" style={{ paddingTop: '16px' }}>
                            <div>
                                <p className="s-row-title">Сброс сессий</p>
                                <p className="s-row-desc">Мгновенно выбросить всех пользователей из системы</p>
                            </div>
                            <button onClick={handleForceLogout} style={{...btnStyle, background: '#ef4444', color: 'white', padding: '8px 16px', fontSize: '0.9rem'}}><PowerOff size={16}/> Выйти всем</button>
                        </div>
                    </div>
                </div>

                <div className="s-card">
                    <div className="s-card-header h-indigo">
                        <Shield size={20} /> <h4 className="s-card-title">Безопасность и Сессии</h4>
                    </div>
                    <div className="s-card-body">
                        <div className="s-row">
                            <div><p className="s-row-title">Время жизни сессии</p><p className="s-row-desc">Часов бездействия до "сгорания" токена</p></div>
                            <select style={{...inputStyle, width: '130px', margin: 0, padding: '8px'}} value={settings.session_timeout} onChange={e => handleSettingChange('session_timeout', parseInt(e.target.value))}>
                                <option value="1">1 час</option><option value="4">4 часа</option><option value="12">12 часов</option><option value="24">24 часа</option><option value="168">7 дней</option>
                            </select>
                        </div>
                        <div className="s-row" style={{ paddingTop: '16px' }}>
                            <div><p className="s-row-title">Лимит загрузки (Upload Limit)</p><p className="s-row-desc">Макс. размер файлов (МРТ, УЗИ)</p></div>
                            <select style={{...inputStyle, width: '130px', margin: 0, padding: '8px'}} value={settings.max_upload_size} onChange={e => handleSettingChange('max_upload_size', parseInt(e.target.value))}>
                                <option value="5">5 MB</option><option value="15">15 MB</option><option value="50">50 MB</option><option value="100">100 MB</option>
                            </select>
                        </div>
                        <div className="s-row" style={{ paddingTop: '16px' }}>
                            <div><p className="s-row-title">Мин. длина пароля</p></div>
                            <input type="number" style={{...inputStyle, width: '130px', margin: 0, padding: '8px', textAlign: 'center'}} min="6" max="20" value={settings.min_password_length} onChange={e => handleSettingChange('min_password_length', parseInt(e.target.value))} />
                        </div>
                    </div>
                </div>

                <div className="s-card">
                    <div className="s-card-header h-cyan">
                        <Network size={20} /> <h4 className="s-card-title">Защита по IP</h4>
                    </div>
                    <div className="s-card-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '12px' }}>Укажите IP-адреса через запятую, с которых разрешен доступ. Если пусто — доступ открыт всем.</p>
                        <textarea 
                            style={{...inputStyle, flex: 1, minHeight: '80px', fontFamily: 'monospace', marginBottom: '12px', resize: 'vertical'}} 
                            placeholder="192.168.1.1, 10.0.0.5, 8.8.8.8" 
                            value={settings.allowed_ips} 
                            onChange={e => setSettings({...settings, allowed_ips: e.target.value})} 
                        />
                        <button onClick={() => handleIntegrationSave('IP-фильтров')} style={{...btnStyle, background: '#0369a1', color: 'white', width: '100%', justifyContent: 'center'}}>Сохранить список IP</button>
                    </div>
                </div>

                <div className="s-card">
                    <div className="s-card-header h-orange">
                        <MessageSquare size={20} /> <h4 className="s-card-title">SMS-шлюз (Payom.tj API)</h4>
                    </div>
                    <div className="s-card-body">
                        <div className="s-row" style={{ background: '#fff7ed', padding: '12px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fed7aa' }}>
                            <div><p style={{ fontWeight: '600', color: '#9a3412', margin: '0 0 4px 0', fontSize: '0.9rem' }}>Отправка SMS</p><p style={{ fontSize: '0.8rem', color: '#c2410c', margin: 0 }}>API Payom.tj для уведомлений</p></div>
                            <Toggle checked={settings.sms_enabled} onChange={v => setSettings({...settings, sms_enabled: v})} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '12px' }}>
                            <div><label style={labelStyle}>API Token (Bearer)</label><input type="password" style={{...inputStyle, padding: '8px 12px'}} placeholder="eyJhbGciOiJIUzI1Ni..." value={settings.sms_token} onChange={e => setSettings({...settings, sms_token: e.target.value})} /></div>
                            <div><label style={labelStyle}>Имя отправителя</label><input type="text" style={{...inputStyle, padding: '8px 12px'}} placeholder="payom.tj" value={settings.sms_sender} onChange={e => setSettings({...settings, sms_sender: e.target.value})} /></div>
                        </div>
                        <button onClick={() => handleIntegrationSave('SMS-шлюза')} style={{...btnStyle, background: '#ea580c', color: 'white', width: '100%', justifyContent: 'center'}}>Сохранить настройки SMS</button>
                    </div>
                </div>

                <div className="s-card">
                    <div className="s-card-header h-green">
                        <Mail size={20} /> <h4 className="s-card-title">Почтовый сервер (SMTP)</h4>
                    </div>
                    <div className="s-card-body">
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div><label style={labelStyle}>SMTP Host</label><input type="text" style={{...inputStyle, padding: '8px 12px'}} placeholder="smtp.gmail.com" value={settings.smtp_host} onChange={e => setSettings({...settings, smtp_host: e.target.value})} /></div>
                            <div><label style={labelStyle}>Port</label><input type="number" style={{...inputStyle, padding: '8px 12px'}} placeholder="587" value={settings.smtp_port} onChange={e => setSettings({...settings, smtp_port: parseInt(e.target.value)})} /></div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div><label style={labelStyle}>Логин (Email)</label><input type="email" style={{...inputStyle, padding: '8px 12px'}} placeholder="noreply@med.tj" value={settings.smtp_user} onChange={e => setSettings({...settings, smtp_user: e.target.value})} /></div>
                            <div><label style={labelStyle}>Пароль</label><input type="password" style={{...inputStyle, padding: '8px 12px'}} placeholder="••••••••" value={settings.smtp_pass} onChange={e => setSettings({...settings, smtp_pass: e.target.value})} /></div>
                        </div>
                        <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Отправитель (Имя и Email)</label><input type="text" style={{...inputStyle, padding: '8px 12px'}} placeholder="eDukhtur Support <noreply@med.tj>" value={settings.smtp_from} onChange={e => setSettings({...settings, smtp_from: e.target.value})} /></div>
                        <button onClick={() => handleIntegrationSave('почты')} style={{...btnStyle, background: '#047857', color: 'white', width: '100%', justifyContent: 'center'}}>Сохранить SMTP</button>
                    </div>
                </div>

                <div className="s-card">
                    <div className="s-card-header h-amber">
                        <FileSpreadsheet size={20} /> <h4 className="s-card-title">Экспорт данных</h4>
                    </div>
                    <div className="s-card-body">
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '16px' }}>Выгрузка списков в формате Excel (CSV) в реальном времени.</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <button onClick={() => handleExportCSV('hospitals', '/hospitals', 'Hospitals')} style={exportBtnStyle}>🏥 Больницы</button>
                            <button onClick={() => handleExportCSV('doctors', '/users?role=doctor', 'Doctors')} style={exportBtnStyle}>👨‍⚕️ Врачи</button>
                            <button onClick={() => handleExportCSV('patients', '/patients', 'Patients')} style={exportBtnStyle}>🧑‍🤝‍🧑 Пациенты</button>
                            <button onClick={() => handleExportCSV('pharmacies', '/users?role=pharmacy', 'Pharmacies')} style={exportBtnStyle}>💊 Аптеки</button>
                            <button onClick={() => handleExportCSV('labs', '/users?role=lab', 'Laboratories')} style={exportBtnStyle}>🔬 Лаборатории</button>
                            <button onClick={() => handleExportCSV('prescriptions', '/prescriptions', 'Prescriptions')} style={exportBtnStyle}>📝 Рецепты</button>
                        </div>
                    </div>
                </div>

                <div className="s-card">
                    <div className="s-card-header h-teal">
                        <Database size={20} /> <h4 className="s-card-title">Резервная копия (Дамп)</h4>
                    </div>
                    <div className="s-card-body">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><HardDrive size={28} color="#059669" /><div><p style={{ margin: '0 0 2px 0', fontSize: '0.85rem', color: '#065f46', fontWeight: '500' }}>Размер хранилища</p><p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#047857' }}>{dbSize}</p></div></div>
                            <div style={{ textAlign: 'right' }}><p style={{ margin: '0 0 2px 0', fontSize: '0.8rem', color: '#065f46' }}>СУБД:</p><p style={{ margin: 0, fontSize: '0.9rem', color: '#065f46', fontWeight: 'bold' }}>PostgreSQL 15</p></div>
                        </div>
                        <div>
                            <button onClick={handleDatabaseDump} disabled={dumpProgress !== null} style={{...btnStyle, background: '#0f766e', color: 'white', width: '100%', justifyContent: 'center'}}>
                                <Download size={18} /> {dumpProgress !== null ? 'Подготовка дампа...' : 'Скачать дамп (SQL)'}
                            </button>
                            {dumpProgress !== null && (
                                <div style={{ marginTop: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#4b5563', marginBottom: '6px', fontWeight: '600' }}><span>Сбор таблиц...</span><span>{dumpProgress}%</span></div>
                                    <div style={{ width: '100%', height: '6px', background: '#e5e7eb', borderRadius: '10px', overflow: 'hidden' }}><div style={{ width: `${dumpProgress}%`, height: '100%', background: dumpProgress === 100 ? '#10b981' : '#0f766e', transition: 'width 0.3s ease' }}></div></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#4b5563', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.95rem', color: '#111827', background: 'white' };
const btnStyle = { border: 'none', borderRadius: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', padding: '10px 16px' };
const exportBtnStyle = { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '10px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s' };

export default Settings;